import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
} from "chart.js";
import { Bubble } from "react-chartjs-2";
import zoomPlugin from 'chartjs-plugin-zoom';
import { Button } from "rsuite";
import annotationPlugin from 'chartjs-plugin-annotation';
import { numberFormatter, numberFormatterForCPGraph, setTicks } from "../../../services/utils";
import ResetZoomButton from "../../../components/ResetZoomButton";
import useGraphFontSize from "../../../hooks/useGraphFontSize";


ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, zoomPlugin, annotationPlugin);

const zoomOptions = {
    pan: {
        enabled: true,
        mode: 'xy',
        modifierKey: 'ctrl',
    },
    zoom: {
        mode: 'xy',
        drag: {
            enabled: true,
            borderColor: 'rgb(54, 162, 235)',
            borderWidth: 1,
            backgroundColor: 'rgba(54, 162, 235, 0.3)'
        }
    }
};

type Props = {
    scatterGraphData: any,
    thresholdX: number,
    thresholdY: number,
    selectedPC: number,
    selectedCustomer: number,
    isPCGraph: boolean,
    filterExpansion: boolean
    handleClick?: () => {},
}


export function CustomerScatterWithZoom({ scatterGraphData, thresholdX, thresholdY, selectedPC, selectedCustomer, isPCGraph, filterExpansion, handleClick }: Props) {
    const axisTicks = useRef({ xTicks: [0], yTicks: [0] });
    const graphData = useRef<{ x: number, y: number, r: number }[]>([]);
    const labels = useRef<string[]>([])
    const backgroundColor = useRef<string[]>([]);
    const max = useRef(0);
    const [graphHeight, setGraphHeight] = useState<number>(250);
    const fontSize = useGraphFontSize()
    useEffect(() => {

        setGraphHeight(filterExpansion ? 250 : 215);
    }, [filterExpansion]);
    useMemo(() => {
        //Axis ticks logic
        const obj = scatterGraphData?.reduce((acc: any, currentObj: any) => {
            acc.avginvoice_amount$.push(currentObj.avginvoice_amount$);
            acc.GP.push(currentObj.GP);
            return acc;
        }, { 'avginvoice_amount$': [], 'GP': [] });
        const minXaxis = Math.min(...obj.avginvoice_amount$);
        const maxXaxis = Math.max(...obj.avginvoice_amount$);
        const minYaxis = Math.min(...obj.GP);
        const maxYaxis = Math.max(...obj.GP);
        const yTicks = setTicks(minYaxis, maxYaxis);
        const xTicks = setTicks(minXaxis, maxXaxis);
        axisTicks.current = { yTicks, xTicks }


        //max logic
        scatterGraphData?.map((d: any) => {
            if (d.sales$ > max.current) {
                max.current = d.sales$
            }
        })

        //graphdata labels and background color
        graphData.current = [];
        labels.current = [];
        backgroundColor.current = []
        scatterGraphData?.map((d: any) => {
            graphData.current.push({ x: d.avginvoice_amount$, y: d.GP, r: (d.sales$ / max.current) * 20 + 5 })
            labels.current.push(d.customer)
            if (selectedPC && selectedPC === d.price_pc) {
                backgroundColor.current.push('#86BC25');
            } else if (selectedCustomer && selectedCustomer === d.customer) {
                backgroundColor.current.push('#86BC25');
            } else {
                backgroundColor.current.push("#EAA614")
            }
        })

    }, [scatterGraphData]);

    const data: any = {
        labels: labels.current,
        datasets: [
            {
                label: "Size->Sales($)",
                data: graphData.current,
                backgroundColor: backgroundColor.current
            }
        ]
    };
    const options: any = {
        onClick: function (e: any) {
            // props.handleClick(props.isPCGraph, props.data[e.chart._active[0].index]);
        },
        responsive: true,
        maintainAspectRatio: false,
        pointRadius: 8,
        plugins: {
            annotation: {
                annotations: {
                    line1: {
                        type: 'line',
                        yMin: graphData.current.length === 0 ? 0 : thresholdX,
                        yMax: graphData.current.length === 0 ? 0 : thresholdX,
                        borderDash: [4],
                        borderColor: "#bebebe",
                        borderWidth: [1],
                        label: {
                            display: false,
                            content: 'Please select a value from date filter',
                            position: "center",
                            font: {
                                size: fontSize,
                                weight: 400,
                            },
                            color: "black",
                        },
                    },
                    line2: {
                        type: 'line',
                        xMax: graphData.current.length === 0 ? 0 : thresholdY,
                        xMin: graphData.current.length === 0 ? 0 : thresholdY,
                        borderDash: [4],
                        borderColor: "#bebebe",
                        borderWidth: [1],
                    }

                }
            },
            zoom: zoomOptions,
            tooltip: {
                filter: function (tooltipItem: any) {
                    return scatterGraphData?.[tooltipItem.dataIndex]
                },
                callbacks: {
                    title: (context: any) => {
                        let element = scatterGraphData?.[context[0].dataIndex]
                        return isPCGraph ? "PC: " + element.price_pc : "Customer: " + element.customer;
                    },
                    label: function (context: any) {
                        let element = scatterGraphData?.[context.dataIndex]
                        return [isPCGraph ? "Net Margin($): " + numberFormatterForCPGraph(element.avginvoice_amount$, 1, '$') : "Net Margin/Invoice($): " + numberFormatterForCPGraph(element.avginvoice_amount$, 1, '$'), "Gross Profit Percentage(%): " + numberFormatter(element.GP) + "%", "Sales($): " + "$" + numberFormatter(element.sales$)];
                    },

                    labelTextColor: function (context: any) {
                        return '#d3d3d3';
                    }
                },
                displayColors: false,
                backgroundColor: "#333333"
            },
            legend: {
                display: true,
                labels: {
                    usePointStyle: true,
                    boxWidth: 5,
                    boxHeight: 5,
                    color: "black",
                    font: {
                        size: fontSize,
                        weight: 400,
                    },
                },
                position: "bottom",
                align: "end"

            },
            title: {
                display: true,
                text: "Size->Sales($)"
            },
        },
        scales: {
            x: {
                offset: true,
                border: {
                    color: "black",
                    width: 1,
                },
                beginAtZero: true,
                max: axisTicks.current.xTicks[axisTicks.current.xTicks.length - 1] + 10,
                title: {
                    display: true,
                    text: isPCGraph ? "Net Margin ($)" : "Net Margin/Invoice ($)",
                    color: "black",
                    font: {
                        size: fontSize
                    },
                },
                grid: {
                    display: false
                },
                ticks: {
                    display: true,
                    min: axisTicks.current.xTicks[0],
                    max: axisTicks.current.xTicks[axisTicks.current.xTicks.length - 1],
                    maxTicksLimit: 8,
                    callback: function (val: any) {
                        return numberFormatterForCPGraph(val, 0, '$');
                    },
                    color: "black",
                    font: {
                        size: fontSize
                    },
                },
            },
            y: {
                border: {
                    color: "black",
                    width: 1,
                },
                beginAtZero: true,
                max: axisTicks.current.yTicks[axisTicks.current.yTicks.length - 1] + 10,
                title: {
                    display: true,
                    text: "Gross Profit Percentage (%)",
                    color: "black",
                    font: {
                        size: fontSize
                    },
                },
                grid: {
                    display: false
                },
                ticks: {
                    maxTicksLimit: 6,
                    color: "black",
                    font: {
                        size: fontSize
                    },
                    min: axisTicks.current.yTicks[0],
                    max: axisTicks.current.yTicks[axisTicks.current.yTicks.length - 1],
                    callback: function (value: any) {
                        if (value < 0)
                            return numberFormatterForCPGraph(value, 0) + '%';
                        else
                            return value.toFixed() + '%';
                    }
                }
            }
        }
    };

    const chartRef: any = React.useRef(null);

    const handleResetZoom = () => {
        if (chartRef && chartRef.current) {
            chartRef.current.resetZoom();
        }
    };


    return (
        <div
            className={`relative bg-white h-full w-full text-[fontSizepx] ${filterExpansion ? "" : "pt-4"
                }`}
        >
            <ResetZoomButton onClick={handleResetZoom} />{" "}
            <Bubble
                ref={chartRef}
                options={options}
                data={data}
                height={graphHeight}
            />
        </div>
    )
}
